import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { resource } from '@util/resource';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { SelfAccount } from "../../interfaces/dws/self-account";
import { AuthenticateAdminService } from "./authenticate-admin.service";
import { AccountsService, Login } from '@services/accounts.service';
import { WinerySubscriptionInfo } from '@interfaces/dws/winery-subscription';

interface LoginResponseDWS {
  idToken: string;
  wineryId: string;
  expire: Date;
  details: any;
}

export interface ImpersonationAccountRow {
  accountId: string;
  email: string;
  name: string;
  roleNames: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdminLoginAsWineryService {

  private cachedLogin: Login | null = null;

  constructor(
      private adminAuthService: AuthenticateAdminService,
      private accountsService: AccountsService
  ) {
    this.accountsService.login$.subscribe((login) => {
      this.cachedLogin = login;
      if (this.accountsService.hasActiveSuperAdminSession(login) && login?.authorization?.token) {
        this.persistPrivilegedToken(login.authorization.token);
      }
    });
  }

  loginAs(wineryId: string, token?: string, accountEmail?: string): Observable<any> {
    const adminToken = (token || this.getToken()).replace(/^Bearer\s+/i, '');
    if (!adminToken) {
      return throwError({
        type: 'unauthorized',
        message: 'No privileged session token',
        status: 401,
      });
    }

    this.persistPrivilegedToken(adminToken);

    const body: { wineryId: string; accountEmail?: string } = { wineryId };
    if (accountEmail && accountEmail.trim()) {
      body.accountEmail = accountEmail.trim();
    }
    
    return resource('crm://admin/authorize-as-winery')
      .headers({'Authorization': `Bearer ${adminToken}`})
      .post<LoginResponseDWS>(body, {observe: 'response'})
      .pipe(
        map(response => {
          if (!response.body) {
            throw {
              type: response.type,
              message: "Unknown error",
              cause: response,
              status: response.status,
            };
          }
          var token = response.body.idToken;
          var wineryId = response.body.wineryId;
          var expires_at = response.body.expire;
          var details = response.body.details;
          const subs = details?.winerySubscriptions as WinerySubscriptionInfo[] | undefined;
          const winerySubscriptions = Array.isArray(subs)
            ? subs.map((s) => ({ ...s }))
            : undefined;

          return {
            wineryId: wineryId,
            account: details,
            userDWS: details,
            winerySubscriptions,
            company: {id: wineryId},
            authorization: {
              expires_at: expires_at,
              token: response.headers.get("Authorization") as string,
              winery_id: wineryId,
              is_admin: true,
              is_winery: true,
            },
          } as Login;
        }),
        mergeMap((data: Login) => {
          return resource("v2://host")
            .authorization(
              "bearer",
              data.authorization.token.replace("Bearer ", "")
            )
            .get<any>()
            .pipe(
              map((hostData) => {
                let company = { ...hostData.company };
                company.id = data.wineryId;
                return { ...data, company, account: { ...hostData } };
              }),
              catchError(() => {
                // Return a null object or a default object structure on error
                //return of({ ...data, company: {}, account: {}});
                return of(data);
              })
            );
        }),
        mergeMap((data: Login) => {
          return resource("crm://self/account")
            .authorization(
              "bearer",
              data.authorization.token.replace("Bearer ", "")
            )
            .get<SelfAccount>()
            .pipe(
              map((selfAccount) => {
                return { ...data, selfAccount };
              }),
              catchError(() => {
                // Return a null object or a default object structure on error
                return of(data);
              })
            );
        }),
        catchError(e => {
          if (e instanceof HttpErrorResponse) {
            if (e.status === 401) {
              return throwError({
                type: 'unauthorized',
                message: 'Invalid credentials',
                cause: e,
                status: e.status
              });
            }

            if (e.status === 404) {
              return throwError({
                type: 'notmigrated',
                message: 'Account not migrate',
                cause: e,
                status: e.status
              });
            }
          }
          return throwError({
            type: 'unknown',
            message: 'Unknown error',
            cause: e,
            status: e.status
          });
        })
      );
  }

  listImpersonationAccounts(wineryId: string, token?: string): Observable<ImpersonationAccountRow[]> {
    const bearer = (token || this.getToken()).replace(/^Bearer\s+/i, '');
    return resource('crm://admin/winery/wineryId/impersonation-accounts')
      .params({ wineryId })
      .bearer(bearer)
      .get<ImpersonationAccountRow[]>();
  }


  private getToken(): string {
    const privileged = this.getOriginalAdminToken();
    return privileged ? privileged.replace(/^Bearer\s+/i, '') : '';
  }

  private persistPrivilegedToken(token: string) {
    const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    localStorage.setItem('original_admin_token', formattedToken);
    if (!this.accountsService.hasActiveSuperAdminSession(this.cachedLogin)) {
      this.adminAuthService.authorization$.next({
        access_token: formattedToken,
        is_admin: true,
        is_winery: false,
      });
    }
  }

  getOriginalAdminToken(): string | null {
    const storedToken = localStorage.getItem('original_admin_token');
    if (storedToken) {
      return storedToken;
    }

    let adminAuthToken: string | null = null;
    this.adminAuthService.authorization$.subscribe((auth) => {
      if (auth?.access_token) {
        adminAuthToken = auth.access_token;
      }
    });
    if (adminAuthToken) {
      return adminAuthToken;
    }

    if (this.accountsService.hasActiveSuperAdminSession(this.cachedLogin)) {
      const sessionToken = this.cachedLogin?.authorization?.token;
      if (sessionToken) {
        return sessionToken.startsWith('Bearer ') ? sessionToken : `Bearer ${sessionToken}`;
      }
    }

    return null;
  }

  clearOriginalAdminToken() {
    // Clear the original admin token when no longer needed
    localStorage.removeItem('original_admin_token');
  }
}
